import { useState } from 'react';
import { Badge, Group, Stack, Title } from '@mantine/core';
import { IconCoinOff, IconTruckOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { SiteFilterSelect, SupplierFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, usePurchasingReport } from '../api';
import { formatDate, formatMoneyCents } from '../format';
import type { PoBookRow, SupplierSpendRow } from '../types';

export interface PurchasingReportProps {
  initialSupplierId?: string;
}

/** Report #6 (BUILD_SPEC.md §13) — open/partial PO book + posted supplier spend.
 * Also the SUPPLIER search-result deep link target (`?supplierId=`). */
export function PurchasingReport({ initialSupplierId }: PurchasingReportProps) {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(initialSupplierId ?? null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = usePurchasingReport({ siteId: siteId ?? undefined, supplierId: supplierId ?? undefined, from, to });
  const openPoBook = query.data?.openPoBook ?? [];
  const supplierSpend = query.data?.supplierSpend ?? [];

  const poColumns: ReportColumn<PoBookRow>[] = [
    { key: 'number', header: 'PO', render: (r) => r.number },
    { key: 'supplier', header: 'Supplier', render: (r) => r.supplierName },
    { key: 'status', header: 'Status', render: (r) => <Badge variant="light">{r.status}</Badge> },
    { key: 'expected', header: 'Expected', render: (r) => formatDate(r.expectedAt) },
    { key: 'lines', header: 'Lines', align: 'right', render: (r) => String(r.lines.length) },
    {
      key: 'progress',
      header: 'Received / ordered',
      align: 'right',
      // MODE-AWARE progress (R7; final audit round 2026-07-07 — the previous
      // all-lines reduce over qtyTonnes/receivedQtyKg was null-blind to EACH
      // lines, so wax/packaging POs showed 0.00/0.00 forever). Mirrors
      // PurchaseOrderRegister.tsx's idiom: a line's mode follows which quantity
      // field is non-null; mass lines aggregate tonnes, EACH lines aggregate
      // units, and a mixed PO renders both figures — never a sum across
      // incompatible units.
      render: (r) => {
        const massLines = r.lines.filter((l) => l.qtyTonnes !== null);
        const eachLines = r.lines.filter((l) => l.qty !== null);
        const parts: string[] = [];
        if (massLines.length > 0) {
          const orderedT = massLines.reduce((sum, l) => sum + Number(l.qtyTonnes), 0);
          const receivedT = massLines.reduce((sum, l) => sum + Number(l.receivedQtyKg) / 1000, 0);
          parts.push(`${receivedT.toFixed(2)}/${orderedT.toFixed(2)} t`);
        }
        if (eachLines.length > 0) {
          const orderedU = eachLines.reduce((sum, l) => sum + Number(l.qty), 0);
          const receivedU = eachLines.reduce((sum, l) => sum + Number(l.receivedQty), 0);
          parts.push(`${receivedU.toFixed(0)}/${orderedU.toFixed(0)} units`);
        }
        return parts.length > 0 ? parts.join(' · ') : '—';
      },
    },
  ];

  const spendColumns: ReportColumn<SupplierSpendRow>[] = [
    { key: 'supplier', header: 'Supplier', render: (r) => r.supplierName },
    { key: 'spend', header: 'Spend', align: 'right', render: (r) => formatMoneyCents(r.spendCents) },
  ];

  return (
    <ReportPage
      title="PO book & supplier spend"
      description="Every OPEN/PARTIAL purchase order, plus posted supplier spend for the selected period."
      action={
        <CsvDownloadButton
          path="/reporting/purchasing"
          query={{
            siteId: siteId ?? undefined,
            supplierId: supplierId ?? undefined,
            ...rangeQuery(from, to),
          }}
          filename="supplier-spend.csv"
          description="Exports the open PO book and posted supplier spend, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={200} />
        <SupplierFilterSelect value={supplierId} onChange={setSupplierId} label="Supplier" w={240} />
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </Group>

      <Stack gap="lg">
        <Stack gap="xs">
          <Title order={4}>Open / partial PO book</Title>
          <ReportTable
            columns={poColumns}
            rows={openPoBook}
            getRowKey={(r) => r.id}
            isLoading={query.isLoading}
            isError={query.isError}
            errorMessage="Failed to load the PO book."
            emptyMessage="No open or partial purchase orders"
            emptyHint="Try a different site or supplier."
            emptyIcon={IconTruckOff}
          />
        </Stack>

        <Stack gap="xs">
          <Title order={4}>Supplier spend (posted, in period)</Title>
          <ReportTable
            columns={spendColumns}
            rows={supplierSpend}
            getRowKey={(r) => r.supplierId}
            isLoading={query.isLoading}
            isError={query.isError}
            errorMessage="Failed to load supplier spend."
            emptyMessage="No posted receipts for the selected filters"
            emptyHint="Try a different site, supplier or date range."
            emptyIcon={IconCoinOff}
          />
        </Stack>
      </Stack>
    </ReportPage>
  );
}
